import { call, fork, take, put, all, actionChannel } from 'redux-saga/effects'
import { buffers, SagaIterator } from 'redux-saga'
import { bindAsyncAction } from 'typescript-fsa-redux-saga'
import { Action } from 'typescript-fsa'
import i18n from 'i18next'

import {
  TagApi,
  GooglePostApi,
  PostTemplateApi,
  PostApi,
  MediaFileApi,
  AboTopicsApi,
  DeferredPostApi,
  IndividualizationApi, UrlInformationExtractorApi,
} from '../../api/instances'
import { Severity, EventType } from '@professocial/professocialTypes/types/messageTypes'
import { requestFailed, requestFinished, requestStarted, setMessage } from '../common/common.actions'

import {
  createPostTemplate,
  createPostTemplateInitiated,
  createTags,
  createTagsInitiated,
  deleteMediaFile,
  deleteMediaFileInitiated,
  findPostTemplatesForCurrentUser,
  findPostTemplatesForCurrentUserInitiated,
  getAllTags,
  getAllTagsInitiated,
  getMediaFileMetadata,
  getMediaFileMetadataInitiated,
  getTopicsAndMetadataForCurrentUser,
  getTopicsAndMetadataForCurrentUserInitiated,
  listDeferredPosts,
  listDeferredPostsInitiated,
  listMediaFiles,
  listMediaFilesInitiated,
  publishPost,
  publishPostInitiated,
  updateDeferredPostInitiated,
  updatePagePostInitiated,
  updatePostTemplate,
  updatePostTemplateInitiated,
  uploadMediaFile,
  uploadMediaFileInitiated,
  uploadTemporaryMediaFile,
  uploadTemporaryMediaFileInitiated,
  updateDeferredPost,
  updatePagePost,
  individualizeText,
  individualizeTextInitiated,
  getDeferredPostById,
  getDeferredPostByIdtInitiated,
  confirmManualDeferredPost,
  confirmManualDeferredPostInitiated,
  deletePostTemplate,
  deletePostTemplateInitiated, getUrlInformation, getUrlInformationInitiated,
} from './posting.actions'
import {
  CreatePostTemplateRequest,
  DeletePostTemplateRequest,
  FindPostTemplatesForCurrentUserLightweightRequest, UpdatePostTemplateRequest,
} from '@3m5/sm_publish-frontend-api/apis/PostTemplateApi'
import { PublishPostRequest } from '@3m5/sm_publish-frontend-api/apis/PostApi'
import { DeleteMediaFileRequest, GetMediaFileMetadataRequest, ListMediaFilesRequest, UploadMediaFileRequest, UploadTemporaryMediaFileRequest } from '@3m5/sm_publish-frontend-api/apis/MediaFileApi'
import { CreateTagsRequest } from '@3m5/sm_publish-frontend-api/apis/TagApi'
import {
  ConfirmManualDeferredPostRequest,
  GetDeferredPostByIdRequest,
  UpdateDeferredPostRequest
} from '@3m5/sm_publish-frontend-api/apis/DeferredPostApi'
import { UpdatePagePostRequest } from '@3m5/sm_publish-frontend-api/apis/GooglePostApi'
import { IndividualizeTextOperationRequest } from '@3m5/sm_publish-frontend-api/apis/IndividualizationApi'
import { GetUrlInformationRequest } from '@3m5/sm_publish-frontend-api/apis/UrlInformationExtractorApi'

// getAllTags
const getAllTagsWorker = bindAsyncAction(getAllTags)(
  function * (): any {
    return yield call([TagApi, TagApi.getAllTags])
  },
)

function * watchGetAllTagsInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(getAllTagsInitiated)
    try {
      yield getAllTagsWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:loadingError', { context: i18n.t('postingCommon:tags') }),
        eventType: EventType.load,
      }))
      console.error('error in watchGetAllTagsInitiated: ', e)
    }
  }
}

// listDeferredPosts
const listDeferredPostsWorker = bindAsyncAction(listDeferredPosts)(
  function * (): any {
    return yield call([DeferredPostApi, DeferredPostApi.listDeferredPosts])
  },
)

function * watchListDeferredPostsInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(listDeferredPostsInitiated)
    yield put(requestFinished({ type: action.type }))
    try {
      yield listDeferredPostsWorker(action.payload)
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:loadingError', { context: i18n.t('postingCommon:instagramPosts') }),
        eventType: EventType.load,
      }))
      yield put(requestFailed({
        type: action.type,
        error: e,
      }))
      console.error('error in watchListDeferredPostsInitiated: ', e)
    }
  }
}

// getTopicsAndMetadataForCurrentUser
const getTopicsAndMetadataForCurrentUserWorker = bindAsyncAction(getTopicsAndMetadataForCurrentUser)(
  function * (): any {
    return yield call([AboTopicsApi, AboTopicsApi.getTopicsAndMetadataForCurrentUser])
  },
)

function * watchGetTopicsAndMetadataForCurrentUserInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(getTopicsAndMetadataForCurrentUserInitiated)
    try {
      yield getTopicsAndMetadataForCurrentUserWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:loadingError', { context: i18n.t('postingCommon:topics') }),
        eventType: EventType.load,
      }))
      console.error('error in watchGetTopicsAndMetadataForCurrentUserInitiated: ', e)
    }
  }
}

// findPostTemplatesForCurrentUser
const findPostTemplatesForCurrentUserWorker = bindAsyncAction(findPostTemplatesForCurrentUser)(
  function * (params: FindPostTemplatesForCurrentUserLightweightRequest): any {
    return yield call([PostTemplateApi, PostTemplateApi.findPostTemplatesForCurrentUserLightweight], params)
  },
)

function * watchFindPostTemplatesForCurrentUserInitiated () {
  while (true) {
    const action: Action<FindPostTemplatesForCurrentUserLightweightRequest> = yield take(findPostTemplatesForCurrentUserInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield findPostTemplatesForCurrentUserWorker(action.payload)
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:loadingError', { context: i18n.t('postingCommon:instagramPosts') }),
        eventType: EventType.load,
      }))
      yield put(requestFailed({
        type: action.type,
        error: e,
      }))
      console.error('error in watchFindPostTemplatesForCurrentUserInitiated: ', e)
    }
  }
}

// publishPost
const publishPostWorker = bindAsyncAction(publishPost)(
  function * (params: PublishPostRequest): any {
    return yield call([PostApi, PostApi.publishPost], params)
  },
)

function * watchPublishPostInitiated () {
  while (true) {
    const action: Action<PublishPostRequest> = yield take(publishPostInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield publishPostWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('postingMessages:isSuccessfullyCreated', { context: i18n.t('postingCommon:instagramPost') }),
          eventType: EventType.create,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postingMessages:createError', { context: i18n.t('postingCommon:instagramPost') }),
          eventType: EventType.create,
        },
      }))
      console.error('error in watchPublishPostInitiated: ', e)
    }
  }
}

// createPostTemplate
const createPostTemplateWorker = bindAsyncAction(createPostTemplate)(
  function * (params: CreatePostTemplateRequest): any {
    return yield call([PostTemplateApi, PostTemplateApi.createPostTemplate], params)
  },
)

function * watchCreatePostTemplateInitiated () {
  while (true) {
    const action: Action<CreatePostTemplateRequest> = yield take(createPostTemplateInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield createPostTemplateWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('postingMessages:isSuccessfullyCreated', { context: i18n.t('postingCommon:template') }),
          eventType: EventType.create,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postingMessages:createError', { context: i18n.t('postingCommon:template') }),
          eventType: EventType.create,
        },
      }))
      console.error('error in watchCreatePostTemplateInitiated: ', e)
    }
  }
}

// updatePostTemplate
const updatePostTemplateWorker = bindAsyncAction(updatePostTemplate)(
  function * (params: UpdatePostTemplateRequest): any {
    return yield call([PostTemplateApi, PostTemplateApi.updatePostTemplate], params)
  },
)

function * watchUpdatePostTemplateInitiated () {
  while (true) {
    const action: Action<UpdatePostTemplateRequest> = yield take(updatePostTemplateInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield updatePostTemplateWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('postingMessages:isSuccessfullyUpdated', { context: i18n.t('postingCommon:template') }),
          eventType: EventType.update,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postingMessages:updateError', { context: i18n.t('postingCommon:template') }),
          eventType: EventType.update,
        },
      }))
      console.error('error in watchupdatePostTemplateInitiated: ', e)
    }
  }
}

// deletePostTemplate
const deletePostTemplateWorker = bindAsyncAction(deletePostTemplate)(
  function * (params: DeletePostTemplateRequest): any {
    return yield call([PostTemplateApi, PostTemplateApi.deletePostTemplate], params)
  },
)

function * watchDeletePostTemplateInitiated () {
  while (true) {
    const action: Action<DeletePostTemplateRequest> = yield take(deletePostTemplateInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.templateId }))
    try {
      yield deletePostTemplateWorker(action.payload)
      yield put(setMessage({
        severity: Severity.success,
        message: i18n.t('postingMessages:isSuccessfullyDeleted', { context: i18n.t('postingCommon:template') }),
        eventType: EventType.delete,
      }))
      yield put(requestFinished({ type: action.type, id: action.payload.templateId }))
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:deleteError', { context: i18n.t('postingCommon:template') }),
        eventType: EventType.update,
      }))
      yield put(requestFailed({ type: action.type, error: e, id: action.payload.templateId }))
      console.error('error in watchDeletePostTemplateInitiated: ', e)
    }
  }
}

// getMediaFileMetadata
const getMediaFileMetadataWorker = bindAsyncAction(getMediaFileMetadata)(
  function * (params: GetMediaFileMetadataRequest): any {
    return yield call([MediaFileApi, MediaFileApi.getMediaFileMetadata], params)
  },
)

function * watchGetMediaFileMetadataInitiated () {
  while (true) {
    const action: Action<GetMediaFileMetadataRequest> = yield take(getMediaFileMetadataInitiated)
    try {
      yield getMediaFileMetadataWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:updateError', { context: i18n.t('postingCommon:getMediaFileMetadataError') }),
        eventType: EventType.update,
      }))
      console.error('error in watchGetMediaFileMetadataInitiated: ', e)
    }
  }
}

// uploadMediaFile
const uploadMediaFileWorker = bindAsyncAction(uploadMediaFile)(
  function * (params: UploadMediaFileRequest): any {
    return yield call([MediaFileApi, MediaFileApi.uploadMediaFile], params)
  },
)

function * watchUploadMediaFileInitiated () {
  while (true) {
    const action: Action<UploadMediaFileRequest> = yield take(uploadMediaFileInitiated)
    try {
      yield uploadMediaFileWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:loadError', { context: i18n.t('postingCommon:mediaFile') }),
        eventType: EventType.update,
      }))
      console.error('error in watchUploadMediaFileInitiated: ', e)
    }
  }
}

// uploadTemporaryMediaFile
const uploadTemporaryMediaFileWorker = bindAsyncAction(uploadTemporaryMediaFile)(
  function * (params: UploadTemporaryMediaFileRequest): any {
    return yield call([MediaFileApi, MediaFileApi.uploadTemporaryMediaFile], params)
  },
)

function * watchUploadTemporaryMediaFileInitiated () {
  while (true) {
    const action: Action<UploadTemporaryMediaFileRequest> = yield take(uploadTemporaryMediaFileInitiated)
    try {
      yield uploadTemporaryMediaFileWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:loadError', { context: i18n.t('postingCommon:temporaryMediaFile') }),
        eventType: EventType.update,
      }))
      console.error('error in watchUploadTemporaryMediaFileInitiated: ', e)
    }
  }
}

// listMediaFiles
const listMediaFilesWorker = bindAsyncAction(listMediaFiles)(
  function * (params: ListMediaFilesRequest): any {
    return yield call([MediaFileApi, MediaFileApi.listMediaFiles], params)
  },
)

function * watchListMediaFilesInitiated () {
  while (true) {
    const action: Action<ListMediaFilesRequest> = yield take(listMediaFilesInitiated)
    try {
      yield listMediaFilesWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:loadError', { context: i18n.t('postingCommon:mediaFiles') }),
        eventType: EventType.load,
      }))
      console.error('error in watchListMediaFilesInitiated: ', e)
    }
  }
}

// deleteMediaFile
const deleteMediaFileWorker = bindAsyncAction(deleteMediaFile)(
  function * (params: DeleteMediaFileRequest): any {
    return yield call([MediaFileApi, MediaFileApi.deleteMediaFile], params)
  },
)

function * watchDeleteMediaFileInitiated () {
  while (true) {
    const action: Action<DeleteMediaFileRequest> = yield take(deleteMediaFileInitiated)
    try {
      yield deleteMediaFileWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:deleteError', { context: i18n.t('postingCommon:mediaFile') }),
        eventType: EventType.delete,
      }))
      console.error('error in watchDeleteMediaFileInitiated: ', e)
    }
  }
}

// getUrlInformation
const getUrlInformationWorker = bindAsyncAction(getUrlInformation)(
  function * (params: GetUrlInformationRequest): any {
    return yield call([UrlInformationExtractorApi, UrlInformationExtractorApi.getUrlInformation], params)
  },
)

function* watchGetUrlInformationInitiated(): SagaIterator {
  const requestChan = yield actionChannel(getUrlInformationInitiated, buffers.expanding(10))

  while (true) {
    const action: Action<{ data: GetUrlInformationRequest; id?: string }> = yield take(requestChan)
    yield put(requestStarted({ type: action.type, id: action.payload.id }))

    try {
      yield call(getUrlInformationWorker, action.payload.data)
      yield put(requestFinished({ type: action.type, id: action.payload.id }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        id: action.payload.id,
        error: e
      }))
      console.error('error in watchGetUrlInformationInitiated: ', e)
    }
  }
}

// createTags
const createTagsWorker = bindAsyncAction(createTags)(
  function * (params: CreateTagsRequest): any {
    return yield call([TagApi, TagApi.createTags], params)
  },
)

function * watchCreateTagsInitiated () {
  while (true) {
    const action: Action<CreateTagsRequest> = yield take(createTagsInitiated)
    try {
      yield createTagsWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postingMessages:creatingError', { context: i18n.t('postingCommon:tagsCreate') }),
        eventType: EventType.create,
      }))
      console.error('error in watchCreateTagsInitiated: ', e)
    }
  }
}

// updateDeferredPost
const updateDeferredPostWorker = bindAsyncAction(updateDeferredPost)(
  function * (params: UpdateDeferredPostRequest): any {
    return yield call([DeferredPostApi, DeferredPostApi.updateDeferredPost], params)
  },
)

function * watchUpdateDeferredPostInitiated () {
  while (true) {
    const action: Action<UpdateDeferredPostRequest> = yield take(updateDeferredPostInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield updateDeferredPostWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('postingMessages:isSuccessfullyUpdated', { context: i18n.t('postingCommon:post') }),
          eventType: EventType.create,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postingMessages:updateError', { context: i18n.t('postingCommon:post') }),
          eventType: EventType.create,
        },
      }))
      console.error('error in watchUpdateDeferredPostInitiated: ', e)
    }
  }
}

// updatePagePost
const updatePagePostWorker = bindAsyncAction(updatePagePost)(
  function * (params: UpdatePagePostRequest): any {
    return yield call([GooglePostApi, GooglePostApi.updatePagePost], params)
  },
)

function * watchUpdatePagePostInitiated () {
  while (true) {
    const action: Action<UpdatePagePostRequest> = yield take(updatePagePostInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield updatePagePostWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('postingMessages:isSuccessfullyUpdated', { context: i18n.t('postingCommon:post') }),
          eventType: EventType.create,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postingMessages:updateError', { context: i18n.t('postingCommon:post') }),
          eventType: EventType.create,
        },
      }))
      console.error('error in watchUpdatePagePostInitiated: ', e)
    }
  }
}

// individualizeText
const individualizeTextWorker = bindAsyncAction(individualizeText)(
  function * (params: IndividualizeTextOperationRequest): any {
    return yield call([IndividualizationApi, IndividualizationApi.individualizeText], params)
  },
)

function * watchIndividualizeTextInitiated () {
  while (true) {
    const action: Action<IndividualizeTextOperationRequest> = yield take(individualizeTextInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield individualizeTextWorker(action.payload)
    } catch (e) {
      console.error('error in watchIndividualizeTextInitiated: ', e)
    }
  }
}

// getDeferredPostById
const getDeferredPostByIdWorker = bindAsyncAction(getDeferredPostById)(
  function * (params: GetDeferredPostByIdRequest): any {
    return yield call([DeferredPostApi, DeferredPostApi.getDeferredPostById], params)
  },
)

function * watchGetDeferredPostByIdInitiated () {
  while (true) {
    const action: Action<GetDeferredPostByIdRequest> = yield take(getDeferredPostByIdtInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield getDeferredPostByIdWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
      }))
    } catch (e: any) {
      if (e?.status === 404) {
        yield put(requestFailed({
          type: action.type,
          message: {
            severity: Severity.error,
            message: i18n.t('postingMessages:getPostError', { context: i18n.t('postingCommon:post') }),
            eventType: EventType.create,
          },
        }))
      }
      console.error('error in watchGetDeferredPostByIdInitiated: ', e)
    }
  }
}

// confirmManualDeferredPost
const confirmManualDeferredPostWorker = bindAsyncAction(confirmManualDeferredPost)(
  function * (params: ConfirmManualDeferredPostRequest): any {
    return yield call([DeferredPostApi, DeferredPostApi.confirmManualDeferredPost], params)
  },
)

function * watchConfirmManualDeferredPostInitiated () {
  while (true) {
    const action: Action<ConfirmManualDeferredPostRequest> = yield take(confirmManualDeferredPostInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield confirmManualDeferredPostWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('postingMessages:isSuccessfullyConfirmed', { context: i18n.t('postingCommon:post') }),
          eventType: EventType.create,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('postingMessages:confirmError', { context: i18n.t('postingCommon:post') }),
          eventType: EventType.create,
        },
        error: e,
      }))
      console.error('error in watchConfirmManualDeferredPostInitiated: ', e)
    }
  }
}

export function * postingSagas () {
  yield fork(watchGetAllTagsInitiated)
  yield fork(watchListDeferredPostsInitiated)
  yield fork(watchGetTopicsAndMetadataForCurrentUserInitiated)
  yield fork(watchFindPostTemplatesForCurrentUserInitiated)
  yield fork(watchPublishPostInitiated)
  yield fork(watchUpdateDeferredPostInitiated)
  yield fork(watchUpdatePagePostInitiated)
  yield fork(watchCreatePostTemplateInitiated)
  yield fork(watchUpdatePostTemplateInitiated)
  yield fork(watchDeletePostTemplateInitiated)
  yield fork(watchGetMediaFileMetadataInitiated)
  yield fork(watchUploadMediaFileInitiated)
  yield fork(watchUploadTemporaryMediaFileInitiated)
  yield fork(watchListMediaFilesInitiated)
  yield fork(watchDeleteMediaFileInitiated)
  yield fork(watchCreateTagsInitiated)
  yield fork(watchIndividualizeTextInitiated)
  yield fork(watchGetDeferredPostByIdInitiated)
  yield fork(watchConfirmManualDeferredPostInitiated)
  yield all([
    watchGetUrlInformationInitiated(),
  ])
}
